import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { changeLanguage, getAvailableLanguages } from '../../services/i18n.js';

export const useI18n = (namespace = 'common') => {
  const { t, i18n, ready } = useTranslation(namespace);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  
  const translate = useCallback((key, options = {}) => {
    if (!ready && options.defaultValue) {
      return options.defaultValue;
    }
    
    const translation = t(key, options);
    
    if (translation === key && namespace !== 'common') {
      const commonTranslation = t(`common:${key}`, options);
      if (commonTranslation !== `common:${key}`) {
        return commonTranslation;
      }
    }
    
    return translation;
  }, [t, ready, namespace]);
  
  const switchLanguage = useCallback(async (lang) => {
    setIsChangingLanguage(true);
    const success = await changeLanguage(lang);
    setIsChangingLanguage(false);
    return success;
  }, []);
  
  const formatDate = useCallback((date, format = 'short') => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
    
    const formats = {
      short: { day: '2-digit', month: '2-digit', year: 'numeric' },
      long: { day: 'numeric', month: 'long', year: 'numeric' },
      time: { hour: '2-digit', minute: '2-digit' },
      full: { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    };
    
    return dateObj.toLocaleString(locale, formats[format] || formats.short);
  }, [i18n.language]);
  
  const plural = useCallback((count, key, options = {}) => {
    return t(key, {
      count,
      ...options,
      defaultValue_one: `${key}_singular`,
      defaultValue_other: `${key}_plural`,
    });
  }, [t]);
  
  const currentLanguage = i18n.language;
  const availableLanguages = getAvailableLanguages();
  
  useEffect(() => {
    const checkLanguageChange = () => {
      const params = new URLSearchParams(window.location.search);
      const langParam = params.get('lang');
      if (langParam && langParam !== i18n.language) {
        changeLanguage(langParam);
      }
    };
    
    checkLanguageChange();
    window.addEventListener('popstate', checkLanguageChange);
    
    return () => {
      window.removeEventListener('popstate', checkLanguageChange);
    };
  }, [i18n.language]);
  
  return {
    t: translate,
    translate,
    switchLanguage,
    isChangingLanguage,
    currentLanguage,
    availableLanguages,
    formatDate,
    plural,
    ready,
    i18n,
  };
};

export const useCommonTranslation = () => useI18n('common');
export const useRecordingTranslation = () => useI18n('recording');
export const useTranscriptionTranslation = () => useI18n('transcription');

export default useI18n;

